import React, { useCallback, useState } from 'react'

import cx from 'classnames'

import { A, Button, Div, H4, P } from '@driftt/tide-core'
import { Table } from '@driftt/tide-table'

import useNotifications from 'hooks/useNotifications'

import { verifySendingDomain } from '../../api'
import DeleteDomainModal from '../DeleteDomainModal'

import './style.css'

const makeDomainTableRow = (dnsEntry) => ({
  data: [
    {
      content: dnsEntry.type,
      className: `dkim-table-row-record-type ${
        dnsEntry.valid
          ? 'dkim-table-row-valid-status'
          : 'dkim-table-row-invalid-status'
      }`,
    },
    { content: dnsEntry.host, className: 'dkim-table-row-dns-host' },
    { content: dnsEntry.data, className: 'dkim-table-row-dns-required-data' },
    {
      content: dnsEntry.valid ? 'Record found' : 'Record not found',
      className: `dkim-table-row-status ${
        dnsEntry.valid
          ? 'dkim-table-row-valid-status'
          : 'dkim-table-row-invalid-status'
      }`,
    },
  ],
})

const getDomainData = (sendingDomain) => {
  if (!!sendingDomain.sparkPostDkim && sendingDomain.sendgridDkim) {
    return sendingDomain.sendgridDkim.dNSRecords
      .concat(sendingDomain.sparkPostDkim.dNSRecords)
      .map(makeDomainTableRow)
  }
  if (sendingDomain.sparkPostDkim) {
    return sendingDomain.sparkPostDkim.dNSRecords.map(makeDomainTableRow)
  }
  if (sendingDomain.sendgridDkim) {
    return sendingDomain.sendgridDkim.dNSRecords.map(makeDomainTableRow)
  }
}

export const DNSSettings = (props) => {
  const [verifyButtonLoading, setVerifyButtonLoading] = useState(false)
  const [verifySuccess, setVerifySuccess] = useState(false)
  const [verifyFail, setVerifyFail] = useState(false)
  const { success, error } = useNotifications()
  const [sendingDomain, setSendingDomain] = useState(props.sendingDomain)
  const [deletingDomain, setDeletingDomain] = useState(null)

  const handleCloseDeleteModal = useCallback(
    () => setDeletingDomain(null),
    [setDeletingDomain]
  )

  const handleDeletedDomain = useCallback(
    (id) => {
      setDeletingDomain(null)
      props.setSendingDomains(
        props.allSendingDomains.filter((d) => d.id !== id)
      )
      success(`Deleted domain ${sendingDomain.domain}`)
    },
    [sendingDomain, props, success]
  )

  const verifyDomain = () => {
    setVerifyButtonLoading(true)
    verifySendingDomain(sendingDomain.id)
      .then((res) => {
        setSendingDomain(res)
        if (res.status === 'VERIFIED') {
          setVerifySuccess(true)
        } else {
          setVerifyFail(true)
        }
      })
      .catch((err) => {
        console.error(err)
        error(
          'An error occurred while attempting to verify your domain. Please try again!'
        )
      })
      .finally(() => setVerifyButtonLoading(false))
  }

  return (
    <>
      <Div>
        <H4>Your DNS settings</H4>
        <P>
          This table shows you the DNS entries needed to connect your domain to
          Drift email. Log in to your domain provider to manage these settings.{' '}
          <A
            href="https://help.drift.com/article/email-sending-domains/"
            target="_blank"
          >
            Learn more
          </A>
        </P>
        <Table
          className="dns-settings-table"
          columns={[
            { label: 'RECORD' },
            { label: 'HOST' },
            { label: 'REQUIRED DATA' },
            { label: 'STATUS' },
          ]}
          data={getDomainData(sendingDomain)}
        />
        <Div className="dns-settings-buttons">
          <Button loading={verifyButtonLoading} onClick={verifyDomain}>
            Verify DNS settings
          </Button>
          <Button
            className="dns-settings-buttons--delete-button"
            type="tertiary"
            onClick={() => setDeletingDomain(true)}
          >
            Delete sending domain
          </Button>
          <P
            className={cx('dkim-domain-verify-helptext', {
              'dkim-domain-verify-fail': verifyFail,
              'dkim-domain-verify-success': verifySuccess,
            })}
          >
            {verifyFail && 'Could not verify your domain'}
            {verifySuccess && 'Domain successfully verified'}
          </P>
        </Div>
      </Div>
      {deletingDomain && (
        <DeleteDomainModal
          sendingDomain={sendingDomain}
          onClose={handleCloseDeleteModal}
          onDelete={handleDeletedDomain}
        />
      )}
    </>
  )
}
